import { ErrorMessage } from "@hookform/error-message";
import i18next from "i18next";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdDelete, MdPersonAdd } from "react-icons/md";
import { toast } from "react-toastify";
import {
    useAllCollaborators,
    useCreateCollaborator,
    useDeleteCollaborator,
} from "../api/hooks/useRiskThemes";
import { useDesignSystem } from "../context/designSystemContext";
import { showErrorToast } from "../utils/errorToast";
import { ConfirmationModal } from "./ConfirmationModal";

interface Collaborator {
  id: number;
  login: string;
  name?: string;
  email?: string;
}

export const ManageCollaborators = () => {
  const { designSystem } = useDesignSystem();
  const { i18n } = useTranslation();
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: number | null;
    login: string;
  }>({
    isOpen: false,
    id: null,
    login: "",
  });

  const { data: collaborators, isLoading } = useAllCollaborators();
  const createCollaboratorMutation = useCreateCollaborator();
  const deleteCollaboratorMutation = useDeleteCollaborator();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      login: "",
      email: "",
    },
  });

  const onSubmit = async (data: {
    name: string;
    login: string;
    email: string;
  }) => {
    try {
      await createCollaboratorMutation.mutateAsync({
        name: data.name,
        login: data.login,
        email: data.email,
      });

      toast.success(i18next.t("collaborator_create_success"));
      reset();
      setShowAddForm(false);
    } catch (error) {
      showErrorToast(error, i18next.t("collaborator_create_error"));
    }
  };

  const handleDeleteClick = (id: number, login: string) => {
    setDeleteModal({
      isOpen: true,
      id,
      login,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.id) return;

    try {
      await deleteCollaboratorMutation.mutateAsync(deleteModal.id);
      toast.success(i18next.t("collaborator_delete_success"));
      setDeleteModal({
        isOpen: false,
        id: null,
        login: "",
      });
    } catch (error) {
      showErrorToast(error, i18next.t("collaborator_delete_error"));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      id: null,
      login: "",
    });
  };

  return (
    <div className="space-y-4">
      {/* Aviso sobre SSO */}
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <p className="font-openSans text-sm text-yellow-800 font-semibold mb-1">
          {i18next.t("collaborator_sso_warning_title")}
        </p>
        <p className="font-openSans text-xs text-yellow-700">
          {i18next.t("collaborator_sso_warning_message")}
        </p>
      </div>

      {/* Botão para adicionar novo colaborador */}
      <div className="flex justify-between items-center">
        <h3
          className="font-openSans font-semibold text-lg"
          style={{ color: designSystem?.cssBasics.primaryColor }}
        >
          {i18next.t("collaborators_list_title")}
        </h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-openSans font-semibold text-white text-sm transition-opacity hover:opacity-90"
          style={{
            backgroundColor: designSystem?.cssBasics.secondaryColor,
          }}
        >
          <MdPersonAdd className="w-5 h-5" />
          {i18next.t("collaborator_add_button")}
        </button>
      </div>

      {/* Formulário de adicionar colaborador */}
      {showAddForm && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label
                className="block font-openSans text-sm font-medium mb-1"
                style={{ color: designSystem?.cssBasics.primaryColor }}
              >
                {i18next.t("name")} *
              </label>
              <input
                type="text"
                {...register("name", {
                  required: i18next.t("required_field"),
                  minLength: {
                    value: 3,
                    message: i18next.t("min_3_characters_field"),
                  },
                })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <ErrorMessage
                errors={errors}
                name="name"
                render={({ message }) => (
                  <p className="text-red-500 text-xs mt-1">{message}</p>
                )}
              />
            </div>

            <div>
              <label
                className="block font-openSans text-sm font-medium mb-1"
                style={{ color: designSystem?.cssBasics.primaryColor }}
              >
                {i18next.t("collaborator_login")} *
              </label>
              <input
                type="text"
                {...register("login", {
                  required: i18next.t("required_field"),
                  minLength: {
                    value: 3,
                    message: i18next.t("min_3_characters_field"),
                  },
                })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <ErrorMessage
                errors={errors}
                name="login"
                render={({ message }) => (
                  <p className="text-red-500 text-xs mt-1">{message}</p>
                )}
              />
            </div>

            <div>
              <label
                className="block font-openSans text-sm font-medium mb-1"
                style={{ color: designSystem?.cssBasics.primaryColor }}
              >
                {i18next.t("collaborator_email")} *
              </label>
              <input
                type="email"
                {...register("email", {
                  required: i18next.t("required_field"),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: i18next.t("collaborator_email_invalid"),
                  },
                })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => (
                  <p className="text-red-500 text-xs mt-1">{message}</p>
                )}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  reset();
                  setShowAddForm(false);
                }}
                className="px-4 py-2 rounded-lg font-openSans font-semibold text-gray-700 text-sm border border-gray-300 hover:bg-gray-50"
              >
                {i18next.t("button_cancel")}
              </button>
              <button
                type="submit"
                disabled={createCollaboratorMutation.isPending}
                className="px-4 py-2 rounded-lg font-openSans font-semibold text-white text-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: designSystem?.cssBasics.secondaryColor,
                }}
              >
                {createCollaboratorMutation.isPending
                  ? i18next.t("collaborator_creating")
                  : i18next.t("button_create")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de colaboradores */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-4 text-center text-gray-500">
            {i18next.t("loading_configurations")}
          </div>
        ) : collaborators && collaborators.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {collaborators.map((collaborator: Collaborator) => (
              <div
                key={collaborator.id}
                className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <p className="font-openSans font-semibold text-gray-900">
                    {collaborator.name || collaborator.login}
                  </p>
                  <p className="font-openSans text-sm text-gray-600">
                    {i18next.t("collaborator_login")}: {collaborator.login}
                  </p>
                  {collaborator.email && (
                    <p className="font-openSans text-sm text-gray-600">
                      {i18next.t("collaborator_email")}: {collaborator.email}
                    </p>
                  )}
                </div>
                <button
                  onClick={() =>
                    handleDeleteClick(collaborator.id, collaborator.login)
                  }
                  disabled={deleteCollaboratorMutation.isPending}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={i18next.t("button_delete")}
                >
                  <MdDelete className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500">
            {i18next.t("collaborators_empty")}
          </div>
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={i18next.t("collaborator_delete_modal_title")}
        message={i18next.t("collaborator_delete_confirmation", {
          login: deleteModal.login,
        })}
        confirmText={i18next.t("button_delete")}
        cancelText={i18next.t("button_cancel")}
        isLoading={deleteCollaboratorMutation.isPending}
      />
    </div>
  );
};
